import { useState, useMemo } from 'react';
import { Users, Search, AlertCircle } from 'lucide-react';
import useSWR from 'swr';
import { useAuthStore } from '../hooks/useAuth';
import { useAutoRefreshInterval } from '../hooks/useAutoRefreshInterval';
import { TableSkeleton } from '../components/ui/TableSkeleton';
import { CustomerDrawer } from '../components/customers/CustomerDrawer';
import { CustomerCreateDialog } from '../components/customers/CustomerCreateDialog';
import { useLocation } from 'wouter';

interface Customer {
    id: string;
    name: string;
    email: string;
    phone?: string;
    code?: string;
    status: string;
    createdAt: string;
}

export function Customers() {
    const { token } = useAuthStore();
    const [, setLocation] = useLocation();
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState<keyof Customer>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

    // Auto-refresh subscription
    const refreshInterval = useAutoRefreshInterval();

    // Fetch data
    // Fetch data
    // Fetch data
    // Fetch data
    const { data, error, isLoading } = useSWR<{ customers: Customer[] }>(
        token ? '/api/customers' : null,
        async (url: string) => {
            const headers: Record<string, string> = {
                Authorization: `Bearer ${token}`,
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
                'Accept': 'application/json'
            };

            const empty = () => ({ customers: [] as Customer[] });

            const safeParse = (text: string) => {
                try {
                    return text ? JSON.parse(text) : null;
                } catch (e) {
                    console.warn('[Customers] Failed to parse JSON:', text?.slice(0, 200));
                    return null;
                }
            };

            const normalizeData = (json: any) => {
                if (Array.isArray(json)) return { customers: json };
                if (json && Array.isArray(json.customers)) return { customers: json.customers };
                if (json && json.customers && Array.isArray(json.customers.customers)) return { customers: json.customers.customers };
                if (json && Array.isArray(json.data)) return { customers: json.data };
                if (json && Array.isArray(json.items)) return { customers: json.items };
                if (json && typeof json === 'object') {
                    console.warn('[Customers] Unexpected response keys:', Object.keys(json));
                }
                return empty();
            };

            try {
                let res = await fetch(url, { cache: 'no-store', headers });

                if (res.status === 204) return empty();

                if (res.status === 304) {
                    const bustedUrl = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
                    res = await fetch(bustedUrl, { cache: 'no-store', headers });
                    if (res.status === 304) return empty();
                }

                // Don’t throw — log and return empty so UI renders
                if (!res.ok) {
                    const body = await res.text().catch(() => '');
                    console.warn('[Customers] Non-OK response', {
                        status: res.status,
                        statusText: res.statusText,
                        contentType: res.headers.get('content-type'),
                        body: body.slice(0, 200)
                    });
                    return empty();
                }

                const text = await res.text();
                if (!text?.trim()) return empty();

                const json = safeParse(text);
                return normalizeData(json);
            } catch (e) {
                console.warn('[Customers] Fetcher exception (returning empty)', e);
                return empty();
            }
        },
        {
            refreshInterval
        }
    );

    const customers = data?.customers || [];

    // Filter & Sort
    const filteredCustomers = useMemo(() => {
        return customers
            .filter(c =>
                (c.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (c.code ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (c.email ?? '').toLowerCase().includes(searchQuery.toLowerCase())
            )
            .sort((a, b) => {
                const valA = a[sortField];
                const valB = b[sortField];

                if (valA === undefined || valB === undefined) return 0;

                let compare = 0;
                // Date sorting
                if (sortField === 'createdAt') {
                    const dateA = new Date(valA as string).getTime();
                    const dateB = new Date(valB as string).getTime();
                    compare = dateA - dateB;
                }
                // String sorting
                else if (typeof valA === 'string' && typeof valB === 'string') {
                    compare = valA.localeCompare(valB, undefined, { sensitivity: 'base' });
                }
                // Fallback
                else {
                    compare = (valA as any) < (valB as any) ? -1 : (valA as any) > (valB as any) ? 1 : 0;
                }

                return sortDirection === 'asc' ? compare : -compare;
            });
    }, [customers, searchQuery, sortField, sortDirection]);

    const handleSort = (field: keyof Customer) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    if (isLoading) return <TableSkeleton />;

    if (error) {
        return (
            <div className="p-8 text-center">
                <div className="inline-flex p-4 rounded-full bg-red-500/10 text-red-400 mb-4">
                    <AlertCircle size={32} />
                </div>
                <h2 className="text-xl font-bold mb-2">Failed to load customers</h2>
                <p className="text-muted-foreground">Unable to connect to the customer service.</p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-[1600px] mx-auto flex flex-col min-h-0 h-full">
            {/* Header */}
            <div className="flex justify-between items-center mb-6 shrink-0">
                <div>
                    <h1 className="text-3xl font-bold font-outfit tracking-tight flex items-center gap-3">
                        <Users className="text-primary" /> Customers
                    </h1>
                    <p className="text-muted-foreground mt-1">Manage customer accounts and contacts</p>
                </div>
                <div className="flex gap-2">
                    {/* Actions */}
                    <CustomerCreateDialog
                        onSuccess={() => {
                            if (new URLSearchParams(window.location.search).get('from') === 'onboarding') {
                                setLocation('/dashboard?from=onboarding');
                            }
                        }}
                        onOpenConflict={(conflictId) => {
                            const found = customers.find(c => c.id === conflictId);
                            if (found) setSelectedCustomer(found);
                        }}
                    />
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex gap-4 mb-6 shrink-0">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search customers by name, code, or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg focus:ring-1 focus:ring-primary focus:outline-none transition-all text-foreground"
                    />
                </div>
            </div>

            {/* Content Table */}
            <div className="flex-1 overflow-auto rounded-xl border border-border bg-card/50 backdrop-blur-sm relative shadow-sm">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-muted/50 sticky top-0 z-10 backdrop-blur-md uppercase tracking-wider">
                        <tr>
                            <th className="p-4 font-semibold text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors border-r border-border/20 last:border-r-0"
                                onClick={() => handleSort('name')}>
                                Customer Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th className="p-4 font-semibold text-sm text-muted-foreground border-r border-border/20 last:border-r-0">Code</th>
                            <th className="p-4 font-semibold text-sm text-muted-foreground border-r border-border/20 last:border-r-0">Contact</th>
                            <th className="p-4 font-semibold text-sm text-muted-foreground cursor-pointer hover:text-foreground border-r border-border/20 last:border-r-0"
                                onClick={() => handleSort('status')}>
                                Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th className="p-4 font-semibold text-sm text-muted-foreground cursor-pointer hover:text-foreground border-r border-border/20 last:border-r-0"
                                onClick={() => handleSort('createdAt')}>
                                Join Date {sortField === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {filteredCustomers.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="p-8 text-center text-muted-foreground">
                                    {searchQuery.trim()
                                        ? `No customers found matching "${searchQuery}"`
                                        : "No customers found."}
                                </td>
                            </tr>
                        ) : (
                            filteredCustomers.map((customer) => (
                                <tr
                                    key={customer.id}
                                    onClick={() => {
                                        if (!window.getSelection()?.toString()) {
                                            setSelectedCustomer(customer);
                                        }
                                    }}
                                    className="hover:bg-muted/30 transition-colors group cursor-pointer"
                                >
                                    <td className="p-4 font-medium text-foreground border-r border-border/20 last:border-r-0">
                                        {customer.name}
                                    </td>
                                    <td className="p-4 border-r border-border/20 last:border-r-0">
                                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded text-muted-foreground border border-border">
                                            {customer.code || 'N/A'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-sm text-muted-foreground border-r border-border/20 last:border-r-0">
                                        <div>{customer.email}</div>
                                        <div className="text-xs opacity-75">{customer.phone || '-'}</div>
                                    </td>
                                    <td className="p-4 border-r border-border/20 last:border-r-0">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${customer.status === 'active'
                                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                                            : 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30'
                                            }`}>
                                            {customer.status.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="p-4 text-sm text-muted-foreground font-mono border-r border-border/20 last:border-r-0">
                                        {new Date(customer.createdAt).toLocaleDateString()}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <CustomerDrawer
                customer={selectedCustomer}
                open={!!selectedCustomer}
                onClose={() => setSelectedCustomer(null)}
            />
        </div>
    );
}
